import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, combineLatest, firstValueFrom, map } from 'rxjs';

import { environment } from '@env/environment';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { HttpSharedCoreContext } from '@shared/enums';
import { UserSession } from '@shared/models';
import {
  closeZendeskMessenger,
  formatIdNameTag,
  loginZendeskUser,
  logoutZendeskUser,
  onZendeskMessengerClose,
  onZendeskMessengerOpen,
  onZendeskUnreadMessages,
  openZendeskMessenger,
  setZendeskConversationFields,
  setZendeskConversationTags,
} from '@shared/utils';
import { GenericHttpClientService } from './generic-http-client.service';

/**
 * Owns the Zendesk Messaging widget lifecycle (SSO login, conversation metadata)
 * and drives a custom launcher: the default floating launcher is hidden via CSS
 * (see styles/components/_zendesk.scss) and our sidebar button opens/closes the
 * messenger with `open`/`close`, with the unread count surfaced through
 * `unreadCount$`. Wraps the framework-agnostic helper in `@shared/utils`.
 *
 * We deliberately do NOT use the `messenger:hide`/`show` API to suppress the
 * launcher: `hide` tears the widget down and `show` rebuilds it, adding a visible
 * delay on every open/close. Hiding just the launcher iframe via CSS keeps the
 * widget warm so open/close are instant.
 *
 * The widget is an online-only support surface — like file upload / report
 * download it is gated on network availability via `isAppOnline$`, which already
 * folds in the user-locked offline flag. Offline the sidebar button is hidden,
 * any open window is closed, and login is skipped (`messenger` fails soft anyway
 * since the token endpoint is unreachable).
 *
 * The `userEmail`, `app` and `environment` conversation fields are wired
 * (ids/values from `environment.zendesk`) and stay a no-op until provisioned in
 * Zendesk Admin Center. The remaining custom fields (organisation / appVersion
 * / pageUrl) are not wired yet for the same reason. Conversation tags are
 * applied today.
 */
@Injectable()
export class ZendeskService {
  /** Unread-message count, for the launcher badge. */
  readonly unreadCount$ = new BehaviorSubject<number>(0);
  /** Whether the messenger window is currently open, for the launcher state. */
  readonly isOpen$ = new BehaviorSubject<boolean>(false);

  /**
   * Single source of truth for support availability: online and signed in, plus
   * the `userDetails` that decision was based on. Both the launcher button
   * (sidebar, via `canShowWidget$`) and `syncWidget` gate on this — the latter
   * also needs `userDetails` for `applyContext`, so deriving both here reads the
   * two source streams once instead of subscribing to `userDetails$` twice. The
   * `isAppOnline$` selector already folds in the user-locked offline flag
   * (`state.isAppOnline && !state.isAppLockedInOffline`), so locking offline
   * flips `canShow` to false without a separate check.
   */
  private readonly widgetState$ = combineLatest([
    this.appFacade.query.isAppOnline$,
    this.userDetailsFacade.query.userDetails$,
  ]).pipe(
    map(([isOnline, userDetails]) => ({
      canShow: isOnline && !!userDetails,
      userDetails,
    }))
  );

  /** Whether the support widget may be shown — the boolean the sidebar consumes. */
  readonly canShowWidget$ = this.widgetState$.pipe(
    map(({ canShow }) => canShow)
  );

  /**
   * Wires the (async-loading) Zendesk SSO login once. `reset()` re-arms it on
   * logout. See `guardedOnce` for the retry / anti-stacking semantics.
   */
  private readonly login = this.guardedOnce(() =>
    loginZendeskUser(() => this.fetchMessagingToken())
  );

  /**
   * Wires the launcher event listeners once. They survive logout (the widget
   * keeps them), so unlike `login` this is never reset.
   */
  private readonly launcherListeners = this.guardedOnce(() =>
    Promise.all([
      onZendeskUnreadMessages((count) => this.unreadCount$.next(count)),
      onZendeskMessengerOpen(() => this.isOpen$.next(true)),
      onZendeskMessengerClose(() => this.isOpen$.next(false)),
    ]).then((dispatched) => dispatched.every(Boolean))
  );

  constructor(
    private genericHttpClientService: GenericHttpClientService,
    private appFacade: AppFacade,
    private userDetailsFacade: UserDetailsFacade
  ) {}

  /**
   * Reconcile the widget with the current app state. Safe to call on every
   * navigation: idempotent login + listener registration and re-applies metadata.
   * When ineligible (offline / locked / signed out) it closes the window.
   */
  async syncWidget(): Promise<void> {
    const { canShow, userDetails } = await firstValueFrom(this.widgetState$);

    if (!canShow) {
      this.close();
      // Reset launcher state directly: while offline the `close` event may not
      // round-trip from the (torn-down) widget, so don't rely on it to clear
      // `isOpen$` — otherwise the launcher stays "active" and the next toggle
      // inverts (closes instead of opens).
      this.isOpen$.next(false);
      this.unreadCount$.next(0);
      return;
    }

    void this.login.run();
    void this.launcherListeners.run();
    this.applyContext(userDetails);
  }

  /** Open the messaging window (from the custom launcher). */
  open(): void {
    openZendeskMessenger();
  }

  /** Close the messaging window. */
  close(): void {
    closeZendeskMessenger();
  }

  /** Toggle the messaging window from the custom launcher. */
  toggle(): void {
    if (this.isOpen$.value) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Clear the authenticated identity + metadata. Call on sign-out so the next
   * user on a shared machine doesn't inherit the session.
   */
  logoutUser(): void {
    this.login.reset();
    this.isOpen$.next(false);
    this.unreadCount$.next(0);
    logoutZendeskUser();
  }

  //#region Private methods

  /**
   * Builds a guarded, run-once effect around an async `dispatch` whose boolean
   * resolves `true` only when the command actually reached the (async-loading)
   * widget. Returns `run`/`reset`:
   *
   * - `run()` is idempotent: it dispatches at most once successfully. While a
   *   dispatch is in flight it ignores overlapping calls, so navigations firing
   *   faster than the widget's ~10s ready-poll don't stack up waiters (or, for
   *   login, double-dispatch). A dispatch that gives up before the widget loads
   *   falls back to `idle` so a later `syncWidget` retries.
   * - `reset()` re-arms the effect (used on logout). If a reset lands while a
   *   dispatch is in flight, the in-flight result is discarded rather than
   *   clobbering the reset — otherwise a logout racing a slow login would leave
   *   the guard wedged `done` and the signed-out user re-authenticated.
   */
  private guardedOnce(dispatch: () => Promise<boolean>): {
    run: () => Promise<void>;
    reset: () => void;
  } {
    let state: 'idle' | 'inflight' | 'done' = 'idle';
    return {
      run: async () => {
        if (state !== 'idle') {
          return;
        }
        state = 'inflight';
        const dispatched = await dispatch();
        if (state === 'inflight') {
          state = dispatched ? 'done' : 'idle';
        }
      },
      reset: () => {
        state = 'idle';
      },
    };
  }

  private applyContext(userDetails: UserSession): void {
    const selectedClient = userDetails.clientsList?.find(
      (client) => client.id === userDetails.selectedClientId
    );
    const selectedSubproject =
      userDetails.selectedSubproject ??
      userDetails.subprojects?.find(
        (subproject) => subproject.id === userDetails.selectedSubprojectId
      );

    const tags = [
      formatIdNameTag('client-id', selectedClient?.id),
      formatIdNameTag('client', undefined, selectedClient?.name),
      formatIdNameTag('subproject-id', selectedSubproject?.id),
      formatIdNameTag('subproject', undefined, selectedSubproject?.name),
    ].filter((tag): tag is string => Boolean(tag));

    setZendeskConversationTags(tags);

    // Surface metadata on the ticket via custom conversation fields. Each field
    // must be "Customers can edit" (the widget writes as the end user) or the
    // value is silently ignored. Blank ids/values are dropped by the helper, so
    // a field stays a no-op until provisioned in Zendesk Admin Center.
    //
    // - userEmail: for messaging users authenticated by `external_id`, Zendesk
    //   stores the JWT `email` claim but never displays it in Agent Workspace,
    //   so we push it onto a custom field instead.
    setZendeskConversationFields([
      {
        id: environment.zendesk.userEmailFieldId,
        value: userDetails.email,
      },
      {
        id: environment.zendesk.clientFieldId,
        value: selectedClient?.name,
      },
      {
        id: environment.zendesk.subprojectFieldId,
        value: selectedSubproject?.name,
      },
      {
        id: environment.zendesk.appFieldId,
        value: environment.zendesk.appFieldValue,
      },
      {
        id: environment.zendesk.appVersionFieldId,
        value: environment.appVersion,
      },
      {
        id: environment.zendesk.envFieldId,
        value: environment.zendesk.envFieldValue,
      },
    ]);
  }

  /**
   * Fetch a fresh short-lived Zendesk messaging JWT from coreApi. Never cached —
   * Zendesk re-invokes on expiry / 401. Resolves null on any failure so the
   * widget stays anonymous instead of breaking.
   */
  private fetchMessagingToken(): Promise<string | null> {
    const headers = new HttpHeaders({ skipClientId: 'true' });
    return firstValueFrom(
      this.genericHttpClientService.post<{ token: string }>({
        context: HttpSharedCoreContext.GetZendeskMessagingToken,
        headers,
        body: null,
      })
    )
      .then((response) => response?.token ?? null)
      .catch(() => null);
  }

  //#endregion
}
